import { api } from './client';
import type {
  User, Product, Customer, PaymentPreset, WarrantyPreset, Settings,
  PiHeader, PiDetail, PreviewResponse, MachineConfig,
} from '@/types';

export const AuthApi = {
  login: (email: string, password: string, totp?: string) =>
    api.post<{ accessToken: string; refreshToken: string; user: User }>('/api/auth/login', { email, password, totp }),
  logout: (refreshToken?: string) => api.post('/api/auth/logout', { refreshToken }),
  me: () => api.get<{ user: User }>('/api/auth/me'),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/api/auth/password', { currentPassword, newPassword }),
  enrollMfa: () => api.post<{ secret: string; otpauth: string; qrDataUrl: string }>('/api/auth/mfa/enroll'),
  verifyMfa: (code: string) => api.post('/api/auth/mfa/verify', { code }),
  disableMfa: (password: string) => api.delete<{ ok: true }>('/api/auth/mfa', { password }),
};

export const UserApi = {
  list: () => api.get<User[]>('/api/users'),
  create: (body: any) => api.post<{ id: string }>('/api/users', body),
  update: (id: string, body: any) => api.patch<User>(`/api/users/${id}`, body),
  remove: (id: string) => api.delete<{ ok: true }>(`/api/users/${id}`),
};

export const ProductApi = {
  list: (params?: any) => api.get<any>('/api/products', params),
  get: (id: string) => api.get<Product>(`/api/products/${id}`),
  create: (body: any) => api.post<{ id: string }>('/api/products', body),
  update: (id: string, body: any) => api.patch<Product>(`/api/products/${id}`, body),
  deactivate: (id: string) => api.delete<{ ok: true }>(`/api/products/${id}`),
};

export const MachineConfigApi = {
  list: (params?: any) => api.get<{ data: MachineConfig[] }>('/api/machine-configs', params),
  get: (id: string) => api.get<MachineConfig>(`/api/machine-configs/${id}`),
  create: (body: any) => api.post<{ id: string }>('/api/machine-configs', body),
  update: (id: string, body: any) => api.patch<MachineConfig>(`/api/machine-configs/${id}`, body),
  updatePrice: (id: string, body: any) => api.patch<MachineConfig>(`/api/machine-configs/${id}/price`, body),
  deactivate: (id: string) => api.delete<{ ok: true }>(`/api/machine-configs/${id}`),
};

export const CustomerApi = {
  list: (params?: any) => api.get<any>('/api/customers', params),
  get: (id: string) => api.get<Customer>(`/api/customers/${id}`),
  create: (body: any) => api.post<{ id: string }>('/api/customers', body),
  update: (id: string, body: any) => api.patch<Customer>(`/api/customers/${id}`, body),
  remove: (id: string) => api.delete<{ ok: true }>(`/api/customers/${id}`),
  extractFromImage: (imageDataUrl: string) =>
    api.post<{
      name: string | null; contact_person: string | null; designation: string | null;
      email: string | null; phone: string | null; whatsapp: string | null;
      billing_address: string | null; state: string | null; state_code: string | null;
      gstin: string | null; country: string | null;
    }>('/api/customers/extract-from-image', { image: imageDataUrl }),
};

export const PaymentPresetApi = {
  list: (params?: any) => api.get<PaymentPreset[]>('/api/payment-presets', params),
  create: (body: any) => api.post<{ id: string }>('/api/payment-presets', body),
  update: (id: string, body: any) => api.patch<PaymentPreset>(`/api/payment-presets/${id}`, body),
  remove: (id: string) => api.delete(`/api/payment-presets/${id}`),
};

export const WarrantyPresetApi = {
  list: () => api.get<WarrantyPreset[]>('/api/warranty-presets'),
  create: (body: any) => api.post<{ id: string }>('/api/warranty-presets', body),
  update: (id: string, body: any) => api.patch<WarrantyPreset>(`/api/warranty-presets/${id}`, body),
  remove: (id: string) => api.delete(`/api/warranty-presets/${id}`),
};

export const SettingsApi = {
  get: () => api.get<Settings>('/api/settings'),
  update: (body: any) => api.patch<Settings>('/api/settings', body),
};

export const PiApi = {
  list: (params?: any) => api.get<any>('/api/pis', params),
  get: (id: string) => api.get<PiDetail>(`/api/pis/${id}`),
  create: (body: any) => api.post<{ id: string }>('/api/pis', body),
  update: (id: string, body: any) => api.put<PiHeader>(`/api/pis/${id}`, body),
  preview: (body: any) => api.post<PreviewResponse>('/api/pis/preview', body),
  markAccepted: (id: string) => api.post(`/api/pis/${id}/mark-accepted`),
  markLost: (id: string, reason?: string) => api.post(`/api/pis/${id}/mark-lost`, { reason }),
  delete: (id: string) => api.delete<{ ok: true }>(`/api/pis/${id}`),
  pdf: (id: string) => api.getBlob(`/api/pis/${id}/pdf`),
  sendEmail: (id: string, body?: any) => api.post(`/api/pis/${id}/send/email`, body),
  sendWhatsApp: (id: string, body?: any) => api.post(`/api/pis/${id}/send/whatsapp`, body),
};

export const DashboardApi = {
  pipeline: (params?: any) => api.get<{ currency: string; value: string; count: number }[]>('/api/dashboard/pipeline-value', params),
  funnel: (params?: any) => api.get<{ status: string; count: number }[]>('/api/dashboard/funnel', params),
  conversionRate: (params?: any) => api.get<any[]>('/api/dashboard/conversion-rate', params),
  discountWatch: (params?: any) => api.get<any[]>('/api/dashboard/discount-watch', params),
  mySummary: () => api.get<any[]>('/api/dashboard/my-summary'),
};

export const AuditApi = {
  list: (params?: any) => api.get<any[]>('/api/audit', params),
};
